import { useState, useMemo, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { UserSidebar } from '@/components/Layout/UserSidebar';
import { projectService } from '@/services/projectService';
import { areaService } from '@/services/areaService';
import { logementsService, LogementsSupervisionStatus } from '@/services/logementsService';
import { streamService } from '@/services/streamService';
import { attributeService } from '@/services/attributeService';
import { weatherService } from '@/services/weatherService';
import { DateTimePicker } from '@/components/DateTimePicker';
import { computeChartRange, sanitizeChartValue } from '@/utils/chartRanges';
import { formatParisDateTime, localPickerValueToIso, parseApiDateTime } from '@/utils/dateTime';
import { Area, AttributeType } from '@/types';
import { useDebounce } from '@/hooks/useDebounce';
import { useTheme } from '@/hooks/useTheme';
import { ProjectLogo } from '@/components/ProjectLogo';
import { getProjectMapImageUrl } from '@/utils';

type ApartmentSupervision = {
  apartment: { id: number; name: string; code: string | null; number: string | null };
  floor: { id: number; name: string; code: string | null };
  building: { id: number; name: string; code: string | null };
  box: { id: number | null; name: string | null; isOnline: boolean | null };
  thermostat: { isOnline: boolean | null; enabled: boolean; modeLabel: string };
  metrics: {
    tempInterieur: number | null;
    tempInterieurAttrId: number | null;
    tempConsigne: number | null;
    tempConsigneAttrId: number | null;
    humidity: number | null;
    humidityAttrId: number | null;
    position: 'Ouverte' | 'Fermée';
    operatingStateAttrId: number | null;
    manoeuvres: number | null;
  };
};

export function CartePage() {
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const projectIdInt = projectId ? Number(projectId) : null;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [markerSidebarOpen, setMarkerSidebarOpen] = useState(false);
  const [selectedBuilding, setSelectedBuilding] = useState<Area | null>(null);
  const [selectedFloorId, setSelectedFloorId] = useState<number | null>(null);
  const [apartmentSearch, setApartmentSearch] = useState<string>('');
  const [selectedApartment, setSelectedApartment] = useState<Area | null>(null);
  const [isApartmentModalOpen, setIsApartmentModalOpen] = useState(false);
  const [isMovingMarker, setIsMovingMarker] = useState(false);
  const [movingBuildingId, setMovingBuildingId] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalAttributeId, setModalAttributeId] = useState<number | null>(null);
  const [modalTitle, setModalTitle] = useState<string>('');
  const [modalInstance, setModalInstance] = useState(0);
  const [modalApartment, setModalApartment] = useState<ApartmentSupervision | null>(null);
  const debouncedApartmentSearch = useDebounce(apartmentSearch, 300);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  // Fetch only the minimal project info needed for this page
  const { data: currentProject, isLoading: isLoadingProject, error: projectError } = useQuery({
    queryKey: ['project-map-info', projectIdInt],
    queryFn: () => projectService.getProjectMapInfo(projectIdInt as number),
    enabled: typeof projectIdInt === 'number' && Number.isFinite(projectIdInt),
    retry: 1,
    // Force refetch when projectId changes to avoid stale data
    refetchOnMount: true,
  });

  // Track when project changes to force image reload
  const [projectChangeTimestamp, setProjectChangeTimestamp] = useState<number>(Date.now());

  useEffect(() => {
    if (currentProject?.id) {
      // Force new timestamp when project changes to break browser cache
      setProjectChangeTimestamp(Date.now());
      setImageLoaded(false);
      setImageError(false);
      // Debug: log the project info to verify we have the right data
      console.log('[CartePage] Project changed:', {
        id: currentProject.id,
        title: currentProject.title,
        mapImage: currentProject.mapImage,
      });
    }
  }, [currentProject?.id]);

  const mapImageUrl = useMemo(() => {
    if (!currentProject) {
      return null;
    }
    // Only return URL if project has a mapImage, otherwise return null (no fallback)
    const dynamic = getProjectMapImageUrl(currentProject.mapImage);
    if (!dynamic) {
      return null;
    }
    // Add cache-buster with timestamp to force browser to reload image
    // This prevents showing the previous project's image when switching projects
    const separator = dynamic.includes('?') ? '&' : '?';
    const url = `${dynamic}${separator}_t=${currentProject.id}-${currentProject.mapImage}-${projectChangeTimestamp}`;
    console.log('[CartePage] mapImageUrl:', {
      projectId: currentProject.id,
      mapImage: currentProject.mapImage,
      finalUrl: url,
    });
    return url;
  }, [currentProject?.id, currentProject?.mapImage, projectChangeTimestamp]);

  if (!projectIdInt) {
    return (
      <div className="flex h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <span className="material-icons-outlined text-red-500 text-6xl mb-4">error</span>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Projet requis</h2>
            <p className="text-slate-500 dark:text-slate-400 mb-4">Veuillez sélectionner un projet pour accéder à la carte.</p>
            <button
              onClick={() => navigate('/dashboard', { replace: true })}
              className="px-4 py-2 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors"
            >
              Choisir un projet
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Fetch weather data for the project's city
  const { data: weatherData, isLoading: isLoadingWeather, error: weatherError } = useQuery({
    queryKey: ['weather', currentProject?.city],
    queryFn: () => {
      if (!currentProject?.city) {
        throw new Error('Aucune ville définie pour ce projet');
      }
      return weatherService.getWeatherByCity(currentProject.city);
    },
    enabled: !!currentProject?.city,
    refetchInterval: 10 * 60 * 1000, // Refresh every 10 minutes
    retry: 1,
    onError: (error) => {
      console.error('Erreur météo:', error);
    },
  });

  // Fetch buildings (root areas) - load immediately when project is available
  const { data: buildingsData, isLoading: isLoadingBuildings } = useQuery({
    queryKey: ['areas', currentProject?.id, 'buildings'],
    queryFn: async () => {
      if (!currentProject?.id) return [];
      // Fetch all areas and filter root areas (buildings)
      const allAreas = await areaService.getAreasByProject(currentProject.id, undefined, true);
      // Filter root areas (those without parent)
      return allAreas.filter(area => !area.parent);
    },
    enabled: !!currentProject?.id,
  });

  // IMPORTANT: keep a stable reference (default [] would be re-created each render when data is undefined)
  const buildings = useMemo(() => buildingsData ?? [], [buildingsData]);

  // Load building marker positions from localStorage or calculate default positions
  const [buildingPositions, setBuildingPositions] = useState<Map<number, { x: number; y: number }>>(new Map());

  // Initialize or load building positions
  useEffect(() => {
    if (buildings.length === 0) {
      // Avoid infinite loops: don't set state repeatedly if it's already empty
      setBuildingPositions((prev) => (prev.size === 0 ? prev : new Map()));
      return;
    }

    const savedPositions = localStorage.getItem(`carte-building-positions-${currentProject?.id}`);
    let positions = new Map<number, { x: number; y: number }>();

    if (savedPositions) {
      try {
        const parsed = JSON.parse(savedPositions);
        positions = new Map(Object.entries(parsed).map(([id, pos]: [string, any]) => [Number(id), pos]));
      } catch (e) {
        console.error('Error parsing saved positions:', e);
      }
    }

    // Fill missing positions with default grid layout
    const cols = Math.ceil(Math.sqrt(buildings.length));
    const rows = Math.ceil(buildings.length / cols);
    
    buildings.forEach((building, index) => {
      if (!positions.has(building.id)) {
        const col = index % cols;
        const row = Math.floor(index / cols);
        const x = 0.2 + (col / (cols + 1)) * 0.6;
        const y = 0.2 + (row / (rows + 1)) * 0.6;
        positions.set(building.id, { x, y });
      }
    });

    setBuildingPositions(positions);
  }, [buildings, currentProject?.id]);

  // Save positions to localStorage whenever they change
  useEffect(() => {
    if (buildingPositions.size > 0 && currentProject?.id) {
      const positionsObj = Object.fromEntries(buildingPositions);
      localStorage.setItem(`carte-building-positions-${currentProject.id}`, JSON.stringify(positionsObj));
    }
  }, [buildingPositions, currentProject?.id]);

  // Handle building marker click
  const handleBuildingMarkerClick = (building: Area, e: React.MouseEvent) => {
    if (isMovingMarker) {
      // In moving mode, select this building for moving
      e.stopPropagation();
      setMovingBuildingId(building.id);
    } else {
      // Normal mode: open sidebar
      e.stopPropagation();
      setSelectedBuilding(building);
      setMarkerSidebarOpen(true);
      setApartmentSearch('');
      setSelectedFloorId(null);
    }
  };

  // Place building marker at click position on the map
  const handlePlaceBuildingMarker = (e: React.MouseEvent) => {
    if (!isMovingMarker || !movingBuildingId) return;

    const container = imageContainerRef.current;
    const img = container?.querySelector('img');
    if (!container || !img) return;

    const rect = img.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;

    // Clamp to 0-1 range
    const clampedX = Math.max(0, Math.min(1, x));
    const clampedY = Math.max(0, Math.min(1, y));

    setBuildingPositions((prev) => {
      const newPositions = new Map(prev);
      newPositions.set(movingBuildingId, { x: clampedX, y: clampedY });
      return newPositions;
    });

    setMovingBuildingId(null);
    setIsMovingMarker(false);
    e.stopPropagation();
  };

  // Toggle marker movement mode
  const toggleMarkerMovement = () => {
    setIsMovingMarker(!isMovingMarker);
    setMovingBuildingId(null);
  };

  // Fetch floors for selected building
  const { data: floors = [], isLoading: isLoadingFloors } = useQuery({
    queryKey: ['areas', currentProject?.id, 'building', selectedBuilding?.id, 'floors'],
    queryFn: async () => {
      if (!currentProject?.id || !selectedBuilding?.id) return [];
      return await areaService.getAreasByProject(
        currentProject.id,
        undefined,
        true,
        `/api/areas/${selectedBuilding.id}`
      );
    },
    enabled: !!currentProject?.id && !!selectedBuilding?.id && markerSidebarOpen,
  });

  const isSearchMode = apartmentSearch.trim().length >= 3;

  // Default mode: load apartments only when a floor is expanded
  const { data: floorApartments = [], isLoading: isLoadingFloorApartments } = useQuery({
    queryKey: ['areas', currentProject?.id, 'floor', selectedFloorId, 'apartments'],
    queryFn: async () => {
      if (!currentProject?.id || !selectedFloorId) return [];
      return await areaService.getAreasByProject(
        currentProject.id,
        undefined,
        true,
        `/api/areas/${selectedFloorId}`
      );
    },
    enabled: !!currentProject?.id && !!selectedFloorId && markerSidebarOpen && !isSearchMode,
  });

  // Search mode: fetch matching apartments via API, hide the tree
  const { data: searchedApartments = [], isLoading: isLoadingSearchApartments } = useQuery({
    queryKey: ['areas', currentProject?.id, 'building', selectedBuilding?.id, 'apartmentSearch', debouncedApartmentSearch],
    queryFn: async () => {
      if (!currentProject?.id || !selectedBuilding?.id) return [];
      const term = debouncedApartmentSearch.trim();
      if (term.length < 3) return [];

      const results = await areaService.getAreasByProject(currentProject.id, term, true);
      const buildingCode = selectedBuilding.code;

      return results
        .filter((a) => a.code?.startsWith(`${buildingCode}-ETAGE-`) && a.code?.includes('-APP-'))
        .sort((a, b) => (a.number || '').localeCompare(b.number || '', 'fr', { numeric: true }));
    },
    enabled: !!currentProject?.id && !!selectedBuilding?.id && markerSidebarOpen && isSearchMode,
  });

  // Fetch supervision data for selected apartment
  const { data: apartmentSupervisionData, isLoading: isLoadingSupervision } = useQuery({
    queryKey: ['apartment-supervision', currentProject?.id, selectedApartment?.id],
    queryFn: async () => {
      if (!currentProject?.id || !selectedApartment?.id) return null;
      const response = await logementsService.getSupervision({
        projectId: currentProject.id,
        apartmentId: selectedApartment.id,
        page: 1,
        itemsPerPage: 1,
      });
      // Return the first (and only) item since we filtered by apartmentId
      return response.items[0] || null;
    },
    enabled: !!currentProject?.id && !!selectedApartment?.id && isApartmentModalOpen,
  });

  // Fetch supervision data for all apartments in the selected building/floor to display temperatures
  const { data: allApartmentsSupervision } = useQuery({
    queryKey: ['all-apartments-supervision', currentProject?.id, selectedBuilding?.id, selectedFloorId, isSearchMode],
    queryFn: async () => {
      if (!currentProject?.id) return [];
      const response = await logementsService.getSupervision({
        projectId: currentProject.id,
        buildingId: selectedBuilding?.id ?? undefined,
        floorId: selectedFloorId ?? undefined,
        page: 1,
        itemsPerPage: 500,
      });
      return response.items;
    },
    enabled: !!currentProject?.id && !!selectedBuilding?.id && markerSidebarOpen,
  });

  // Building stats: logements count + offline boxes count (paginate to avoid truncation)
  const { data: buildingStats } = useQuery({
    queryKey: ['building-stats', currentProject?.id, selectedBuilding?.id],
    queryFn: async () => {
      if (!currentProject?.id || !selectedBuilding?.id) return null;

      const itemsPerPage = 500;
      let page = 1;
      let totalItems = 0;
      const allItems: Array<any> = [];

      while (true) {
        const response = await logementsService.getSupervision({
          projectId: currentProject.id,
          buildingId: selectedBuilding.id,
          status: 'all',
          page,
          itemsPerPage,
        });

        if (page === 1) {
          totalItems = response.totalItems;
        }

        allItems.push(...response.items);

        if (response.items.length < itemsPerPage) break;
        if (allItems.length >= response.totalItems) break;

        page += 1;
        if (page > 20) break; // safety
      }

      return { totalItems, items: allItems };
    },
    enabled: !!currentProject?.id && !!selectedBuilding?.id && markerSidebarOpen,
    staleTime: 60_000,
  });

  const offlineBoxesCount = useMemo(() => {
    const items = buildingStats?.items ?? [];
    const offlineBoxIds = new Set<number>();
    for (const item of items) {
      const boxId = item?.box?.id as number | null | undefined;
      const isOnline = item?.box?.isOnline as boolean | null | undefined;
      if (boxId && isOnline === false) {
        offlineBoxIds.add(boxId);
      }
    }
    return offlineBoxIds.size;
  }, [buildingStats]);

  // Create a map for quick temperature lookup by apartment ID
  const apartmentTemperatures = useMemo(() => {
    if (!allApartmentsSupervision) return new Map<number, number | null>();
    const tempMap = new Map<number, number | null>();
    allApartmentsSupervision.forEach((item) => {
      tempMap.set(item.apartment.id, item.metrics.tempInterieur);
    });
    return tempMap;
  }, [allApartmentsSupervision]);

  // Auto-open StreamModal with default attribute when supervision data is loaded
  useEffect(() => {
    if (apartmentSupervisionData && isApartmentModalOpen && currentProject && !modalOpen) {
      // Select Temp. intérieur by default, or first available attribute
      const defaultAttrId = apartmentSupervisionData.metrics.tempInterieurAttrId
        || apartmentSupervisionData.metrics.tempConsigneAttrId
        || apartmentSupervisionData.metrics.humidityAttrId
        || apartmentSupervisionData.metrics.operatingStateAttrId;

      const defaultTitle = apartmentSupervisionData.metrics.tempInterieurAttrId
        ? 'Temp. intérieur'
        : apartmentSupervisionData.metrics.tempConsigneAttrId
          ? 'Temp. consigne'
          : apartmentSupervisionData.metrics.humidityAttrId
            ? 'Humidité'
            : 'Vanne radiateur';

      if (defaultAttrId) {
        setModalApartment(apartmentSupervisionData);
        setModalAttributeId(defaultAttrId);
        setModalTitle(defaultTitle);
        setModalOpen(true);
        setModalInstance((v) => v + 1);
      }
    }
  }, [apartmentSupervisionData, isApartmentModalOpen, currentProject, modalOpen]);


  // Auto-select first building when buildings load
  useEffect(() => {
    if (buildings.length > 0 && !selectedBuilding && markerSidebarOpen) {
      setSelectedBuilding(buildings[0]);
    }
  }, [buildings, selectedBuilding, markerSidebarOpen]);

  // Reset search when closing sidebar or changing building/floor
  useEffect(() => {
    if (!markerSidebarOpen) {
      setApartmentSearch('');
      setSelectedFloorId(null);
    }
  }, [markerSidebarOpen]);

  // Add global mouse event listeners for better panning experience
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const newPanX = e.clientX - dragStart.x;
        const newPanY = e.clientY - dragStart.y;
        
        // Limit panning to keep image within bounds when zoomed
        if (zoomLevel > 1) {
          const container = imageContainerRef.current;
          if (container) {
            const maxPanX = (container.offsetWidth * (zoomLevel - 1)) / 2;
            const maxPanY = (container.offsetHeight * (zoomLevel - 1)) / 2;
            
            setPanX(Math.max(-maxPanX, Math.min(maxPanX, newPanX)));
            setPanY(Math.max(-maxPanY, Math.min(maxPanY, newPanY)));
          } else {
            setPanX(newPanX);
            setPanY(newPanY);
          }
        } else {
          // When zoom is 1, allow free panning (useful for large images)
          setPanX(newPanX);
          setPanY(newPanY);
        }
      }
    };

    const handleGlobalMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleGlobalMouseMove, { passive: true });
      document.addEventListener('mouseup', handleGlobalMouseUp, { passive: true });
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, dragStart, zoomLevel]);

  // Add wheel event listener with passive: false to allow preventDefault
  useEffect(() => {
    const container = imageContainerRef.current;
    if (!container) return;

    const handleWheelEvent = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoomLevel((prevZoom) => {
        const newZoom = Math.max(0.5, Math.min(5, prevZoom + delta));
        // Keep image centered when zooming
        setPanX(0);
        setPanY(0);
        return newZoom;
      });
    };

    container.addEventListener('wheel', handleWheelEvent, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheelEvent);
    };
  }, []);

  // Loading state
  if (isLoadingProject) {
    return (
      <div className="flex h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100">
        <UserSidebar projectId={projectIdInt} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <span className="material-icons-outlined animate-spin text-primary text-6xl mb-4">progress_activity</span>
            <p className="text-slate-500 dark:text-slate-400">Chargement du projet...</p>
          </div>
        </div>
      </div>
    );
  }

  if (projectError) {
    return (
      <div className="flex h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100">
        <UserSidebar projectId={projectIdInt} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-md">
            <span className="material-icons-outlined text-red-500 text-6xl mb-4">error</span>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Projet inaccessible</h2>
            <p className="text-slate-500 dark:text-slate-400 mb-4">Ce projet est introuvable ou vous n'y avez pas accès.</p>
            <button
              onClick={() => navigate('/dashboard', { replace: true })}
              className="px-4 py-2 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors"
            >
              Choisir un projet
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!currentProject) {
    return (
      <div className="flex h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100">
        <UserSidebar projectId={projectIdInt} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <span className="material-icons-outlined text-slate-400 text-6xl mb-4">error</span>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Projet introuvable</h2>
            <p className="text-slate-500 dark:text-slate-400">Ce projet n'existe pas (ou n'est pas accessible).</p>
          </div>
        </div>
      </div>
    );
  }

  const StatusBadge = ({ online, label }: { online: boolean; label: string }) => (
    <span
      className={`px-2 py-1 rounded-md text-xs font-semibold ${
        online ? 'bg-green-500 text-white' : 'bg-slate-300 dark:bg-slate-600 text-slate-700 dark:text-slate-200'
      }`}
    >
      {label}
    </span>
  );

  const MiniSparkline = ({ colorClass }: { colorClass: string }) => (
    <div className="h-4 w-full mt-2 relative overflow-hidden rounded-md bg-slate-200/40 dark:bg-slate-800/40 border border-slate-300/60 dark:border-slate-700/60 hidden md:block">
      <svg viewBox="0 0 120 32" className="absolute inset-0 h-full w-full">
        <polyline
          fill="none"
          className={colorClass}
          strokeWidth="2.5"
          strokeLinejoin="round"
          strokeLinecap="round"
          points="0,26 10,24 20,22 30,19 40,21 50,18 60,16 70,17 80,14 90,15 100,13 110,12 120,10"
        />
      </svg>
      <div className={`absolute inset-0 ${colorClass} opacity-10`} />
    </div>
  );

  const MetricCard = ({
    icon,
    title,
    value,
    unit,
    badge,
    badgeTone = 'slate',
    accent = 'cyan',
    showSparkline = false,
    onClick,
  }: {
    icon: string;
    title: string;
    value: string;
    unit?: string;
    badge?: string;
    badgeTone?: 'green' | 'red' | 'slate' | 'yellow' | 'purple' | 'cyan';
    accent?: 'yellow' | 'cyan' | 'purple' | 'emerald' | 'orange';
    showSparkline?: boolean;
    onClick?: () => void;
  }) => {
    const badgeClass =
      badgeTone === 'green'
        ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-200 border-emerald-500/20'
        : badgeTone === 'red'
          ? 'bg-rose-500/15 text-rose-700 dark:text-rose-200 border-rose-500/20'
          : badgeTone === 'yellow'
            ? 'bg-yellow-500/15 text-yellow-700 dark:text-yellow-200 border-yellow-500/20'
            : badgeTone === 'purple'
              ? 'bg-purple-500/15 text-purple-700 dark:text-purple-200 border-purple-500/20'
              : badgeTone === 'cyan'
                ? 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-200 border-cyan-500/20'
                : 'bg-slate-500/10 text-slate-600 dark:text-slate-200 border-slate-500/10';

    const iconBg =
      accent === 'yellow'
        ? 'bg-yellow-500/15 text-yellow-300 border-yellow-500/20'
        : accent === 'purple'
          ? 'bg-purple-500/15 text-purple-300 border-purple-500/20'
          : accent === 'emerald'
            ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/20'
            : accent === 'orange'
              ? 'bg-orange-500/15 text-orange-300 border-orange-500/20'
              : 'bg-cyan-500/15 text-cyan-300 border-cyan-500/20';

    const sparkColor =
      accent === 'yellow'
        ? 'stroke-yellow-400'
        : accent === 'purple'
          ? 'stroke-purple-400'
          : accent === 'emerald'
            ? 'stroke-emerald-400'
            : accent === 'orange'
              ? 'stroke-orange-400'
              : 'stroke-cyan-400';

    return (
      <div
        className={`relative rounded-2xl border border-slate-200 dark:border-slate-700/70 bg-white dark:bg-slate-900/70 shadow-sm p-2 min-w-0 ${
          onClick ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/80 transition-colors' : ''
        }`}
        onClick={onClick}
        title={onClick ? 'Cliquer pour voir le graphique' : undefined}
      >
        <div className="flex items-start justify-between gap-2 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <div className={`h-8 w-8 rounded-2xl border flex items-center justify-center ${iconBg} shrink-0`}>
              <span className="material-icons-outlined" style={{ fontSize: '16px' }}>
                {icon}
              </span>
            </div>
            <div className="min-w-0">
              <div className="text-[11px] text-slate-600 dark:text-slate-300 font-semibold truncate leading-tight">{title}</div>
              <div className="mt-0.5 flex items-baseline gap-1.5 min-w-0">
                <div
                  className={`font-extrabold tracking-tight text-slate-900 dark:text-white leading-none ${
                    value.length > 8 ? 'text-base' : value.length > 5 ? 'text-lg' : 'text-xl'
                  } truncate max-w-[140px]`}
                  title={value}
                >
                  {value}
                </div>
                {unit ? <div className="text-xs text-slate-500 dark:text-slate-400 font-semibold shrink-0">{unit}</div> : null}
              </div>
            </div>
          </div>
          {badge ? (
            <span
              className={`shrink-0 px-2 py-0.5 rounded-lg border text-[10px] font-bold leading-none ${badgeClass} max-w-[120px] truncate`}
              title={badge}
            >
              {badge}
            </span>
          ) : null}
        </div>
        {showSparkline && <MiniSparkline colorClass={sparkColor} />}
      </div>
    );
  };

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.25, 5)); // Max zoom 5x
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.25, 0.5)); // Min zoom 0.5x
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
    setPanX(0);
    setPanY(0);
    setIsDragging(false);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 0) { // Left mouse button
      if (isMovingMarker && movingBuildingId) {
        // If moving a specific building marker, place it at click position
        handlePlaceBuildingMarker(e);
      } else {
        // Normal pan mode
        setIsDragging(true);
        setDragStart({ x: e.clientX - panX, y: e.clientY - panY });
        e.preventDefault();
        e.stopPropagation();
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      const newPanX = e.clientX - dragStart.x;
      const newPanY = e.clientY - dragStart.y;
      
      // Limit panning to keep image within bounds when zoomed
      if (zoomLevel > 1) {
        const container = imageContainerRef.current;
        if (container) {
          const maxPanX = (container.offsetWidth * (zoomLevel - 1)) / 2;
          const maxPanY = (container.offsetHeight * (zoomLevel - 1)) / 2;
          
          setPanX(Math.max(-maxPanX, Math.min(maxPanX, newPanX)));
          setPanY(Math.max(-maxPanY, Math.min(maxPanY, newPanY)));
        } else {
          setPanX(newPanX);
          setPanY(newPanY);
        }
      } else {
        // When zoom is 1, allow free panning (useful for large images)
        setPanX(newPanX);
        setPanY(newPanY);
      }
      // Don't call preventDefault on mousemove - it's not needed
      e.stopPropagation();
    }
  };

  const handleMouseUp = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsDragging(false);
  };

  // handleWheel is now handled by the useEffect with addEventListener
  // This function is kept for compatibility but won't be used
  const handleWheel = (e: React.WheelEvent) => {
    // This is now handled by the useEffect with addEventListener
    // No need to prevent default here as it's handled in the effect
  };

  // Stream Modal Component
  function StreamModal({
    attributeId,
    title,
    onClose,
    apartment,
    onAttributeChange,
    projectId,
  }: {
    attributeId: number;
    title: string;
    onClose: () => void;
    apartment: ApartmentSupervision | null;
    onAttributeChange: (attrId: number, attrTitle: string) => void;
    projectId: number;
  }) {
    const queryClient = useQueryClient();
    
    const getDefaultDates = () => {
      const defaultTo = new Date();
      const defaultFrom = new Date(defaultTo.getTime() - 24 * 60 * 60 * 1000);
      return {
        from: defaultFrom.toISOString().slice(0, 16),
        to: defaultTo.toISOString().slice(0, 16),
      };
    };
    
    const [dateFrom, setDateFrom] = useState<string>(getDefaultDates().from);
    const [dateTo, setDateTo] = useState<string>(getDefaultDates().to);
    const [dateError, setDateError] = useState<string | null>(null);
    const [isExporting, setIsExporting] = useState(false);
    const [exportProgress, setExportProgress] = useState(0);

    // Invalider le cache quand l'attribut change (garder les dates sélectionnées)
    useEffect(() => {
      // Purge streams cache pour éviter toute donnée résiduelle entre attributs
      queryClient.removeQueries({ queryKey: ['streams'], exact: false });
    }, [attributeId, queryClient]);

    const validateDates = (from: string, to: string): string | null => {
      if (!from || !to) {
        return null;
      }

      const fromDate = new Date(from);
      const toDate = new Date(to);

      if (fromDate >= toDate) {
        return 'La date de début doit être antérieure à la date de fin';
      }

      const diffHours = (toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60);
      if (diffHours < 24) {
        return 'La période doit être d\'au moins 24 heures';
      }

      return null;
    };

    const handleDateFromChange = (value: string) => {
      setDateFrom(value);
      setDateError(validateDates(value, dateTo));
    };

    const handleDateToChange = (value: string) => {
      setDateTo(value);
      setDateError(validateDates(dateFrom, value));
    };

    const fromISO = useMemo(() => {
      if (!dateFrom || dateError) return undefined;
      return localPickerValueToIso(dateFrom);
    }, [dateFrom, dateError]);

    const toISO = useMemo(() => {
      if (!dateTo || dateError) return undefined;
      const [datePart, timePart] = dateTo.split('T');
      const [hours = 0, minutes = 0] = (timePart || '').split(':').map(Number);
      if (hours === 0 && minutes === 0) {
        return localPickerValueToIso(`${datePart}T23:59`);
      }
      return localPickerValueToIso(dateTo);
    }, [dateTo, dateError]);

    const handleExport = async () => {
      if (!fromISO || !toISO || dateError) {
        return;
      }

      if (!projectId) {
        alert('Erreur : Projet non défini. Veuillez réessayer.');
        return;
      }

      setIsExporting(true);
      setExportProgress(0);

      let simulatedProgress = 0;
      const progressInterval = setInterval(() => {
        simulatedProgress += 1;
        const progressPercent = Math.min(90, (simulatedProgress / 100) * 90);
        setExportProgress(progressPercent);
        
        if (simulatedProgress >= 100) {
          clearInterval(progressInterval);
        }
      }, 30);

      try {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        
        clearInterval(progressInterval);
        setExportProgress(90);

        const blob = await streamService.exportCsv(
          projectId,
          attributeId,
          fromISO,
          toISO,
          (progress) => {
            if (progress > 90) {
              setExportProgress(progress);
            }
          }
        );

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        const sanitizeFilename = (str: string) => {
          return str
            .replace(/[<>:"/\\|?*]/g, '')
            .replace(/\s+/g, '_')
            .trim();
        };
        
        const formatDateForFilename = (dateStr: string) => {
          return dateStr.replace('T', '_').replace(/:/g, '-');
        };
        
        const apartmentName = apartment?.apartment.name || 'Appartement';
        const attributeName = title || 'Attribut';
        const dateFromFormatted = formatDateForFilename(dateFrom);
        const dateToFormatted = formatDateForFilename(dateTo);
        
        const filename = `${sanitizeFilename(apartmentName)}_${sanitizeFilename(attributeName)}_${dateFromFormatted}_${dateToFormatted}.csv`;
        
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setExportProgress(100);
        setTimeout(() => {
          setIsExporting(false);
          setExportProgress(0);
        }, 500);
      } catch (error: any) {
        clearInterval(progressInterval);
        console.error('Export error:', error);
        setIsExporting(false);
        setExportProgress(0);
        const errorMessage = error?.message || 'Erreur lors de l\'export. Veuillez réessayer.';
        alert(`Erreur lors de l'export: ${errorMessage}`);
      }
    };

    // Fetch attribute to get unit
    const { data: attribute } = useQuery({
      queryKey: ['attribute', attributeId],
      queryFn: () => attributeService.getAttribute(attributeId),
      enabled: !!attributeId,
    });

    const unit = useMemo(() => {
      if (!attribute) return '';
      const attributeType = typeof attribute.type === 'object' ? attribute.type : null;
      return (attributeType as AttributeType)?.unit || '';
    }, [attribute]);

    const attributeTypeCode = useMemo(() => {
      if (!attribute) return null;
      const attributeType = typeof attribute.type === 'object' ? attribute.type : null;
      return (attributeType as AttributeType)?.code || null;
    }, [attribute]);

    const { data: streams, isLoading, refetch } = useQuery({
      queryKey: ['streams', attributeId, fromISO, toISO],
      queryFn: () =>
        streamService.getAllStreamsByAttribute(attributeId, {
          itemsPerPage: 500,
          order: { timestamp: 'ASC' },
          from: fromISO,
          to: toISO,
        }),
      enabled: !!attributeId && !dateError && !!fromISO && !!toISO,
    });

    const chartData = useMemo(() => {
      if (!streams || streams.length === 0) return [];
      return streams.map((s) => ({
        timestamp: parseApiDateTime(s.timestamp).getTime(),
        value: sanitizeChartValue(parseFloat(s.value), title, unit, attributeTypeCode),
        label: formatParisDateTime(s.timestamp, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }),
      }));
    }, [attributeTypeCode, streams, title, unit]);

    const { min: minValue, max: maxValue, range, dataMin, dataMax } = computeChartRange(
      chartData.map((d) => d.value),
      title,
      unit,
      attributeTypeCode
    );
    const chartHeight = 300;
    const chartWidth = 800;
    const padding = 40;

    const [hoverIndex, setHoverIndex] = useState<number | null>(null);

    const getX = (index: number) => padding + (index / (chartData.length - 1 || 1)) * (chartWidth - 2 * padding);
    const getY = (value: number) => {
      const clamped = Math.max(minValue, Math.min(maxValue, value));
      return chartHeight - padding - ((clamped - minValue) / range) * (chartHeight - 2 * padding);
    };

    const pathData =
      chartData.length > 0
        ? chartData
            .map((d, i) => `${i === 0 ? 'M' : 'L'} ${getX(i)} ${getY(d.value)}`)
            .join(' ')
        : '';

    const hoveredPoint = useMemo(() => {
      if (hoverIndex === null || chartData.length === 0) return null;
      const p = chartData[hoverIndex];
      if (!p) return null;
      return {
        ...p,
        x: getX(hoverIndex),
        y: getY(p.value),
      };
    }, [chartData, hoverIndex, minValue, range, chartWidth, padding]);

    const handleChartMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
      if (chartData.length === 0) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * chartWidth;
      // clamp to plotting area
      const clampedX = Math.max(padding, Math.min(chartWidth - padding, x));
      const ratio = (clampedX - padding) / (chartWidth - 2 * padding);
      const idx = Math.round(ratio * (chartData.length - 1));
      const safeIdx = Math.max(0, Math.min(chartData.length - 1, idx));
      setHoverIndex(safeIdx);
    };

    const handleChartMouseLeave = () => setHoverIndex(null);

    const attributes = apartment
      ? [
          {
            id: apartment.metrics.tempInterieurAttrId,
            title: 'Temp. intérieur',
            icon: 'home',
            value: apartment.metrics.tempInterieur,
            unit: '°C',
            accent: 'orange',
          },
          {
            id: apartment.metrics.tempConsigneAttrId,
            title: 'Temp. consigne',
            icon: 'tune',
            value: apartment.metrics.tempConsigne,
            unit: '°C',
            accent: 'yellow',
          },
          {
            id: apartment.metrics.humidityAttrId,
            title: 'Humidité',
            icon: 'water_drop',
            value: apartment.metrics.humidity,
            unit: '%',
            accent: 'purple',
          },
          {
            id: apartment.metrics.operatingStateAttrId,
            title: 'Vanne radiateur',
            icon: 'settings',
            value: apartment.metrics.position,
            unit: null,
            accent: 'emerald',
          },
        ].filter((attr) => attr.id !== null)
      : [];

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
        <div
          className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-2xl max-w-6xl w-full mx-4 max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-1 min-h-0">
            {/* Sidebar */}
            {apartment && attributes.length > 0 && (
              <div className="w-64 border-r border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 flex flex-col">
                <div className="p-4 border-b border-slate-200 dark:border-slate-700">
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 truncate">{apartment.apartment.name}</p>
                </div>
                  <div className="flex-1 overflow-y-auto p-2">
                    {attributes.map((attr) => {
                      const isActive = attr.id === attributeId;
                      const accentColorClass =
                        attr.accent === 'orange'
                          ? 'bg-orange-400'
                          : attr.accent === 'yellow'
                            ? 'bg-yellow-400'
                            : attr.accent === 'purple'
                              ? 'bg-purple-400'
                              : attr.accent === 'emerald'
                                ? 'bg-emerald-400'
                                : 'bg-cyan-400';
                      return (
                        <button
                          key={attr.id}
                          onClick={() => {
                            if (attr.id) {
                              onAttributeChange(attr.id, attr.title);
                            }
                          }}
                          className={`w-full text-left p-3 rounded-lg mb-2 transition-colors relative ${
                            isActive
                              ? 'bg-primary/20 border border-primary/30 text-primary'
                              : 'bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 hover:border-slate-300 dark:hover:border-slate-600'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className="material-icons-outlined text-lg" style={{ fontSize: '20px' }}>
                              {attr.icon}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold truncate">{attr.title}</div>
                              <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                {attr.value !== null
                                  ? typeof attr.value === 'number'
                                    ? `${attr.value.toFixed(1)}${attr.unit || ''}`
                                    : attr.value
                                  : 'N/A'}
                              </div>
                            </div>
                            {isActive && (
                              <span className="material-icons-outlined text-primary" style={{ fontSize: '18px' }}>
                                check_circle
                              </span>
                            )}
                          </div>
                          <div className={`absolute bottom-0 left-0 right-0 h-0.5 ${accentColorClass} rounded-b-lg`} />
                        </button>
                      );
                    })}
                  </div>
              </div>
            )}

            {/* Main Content */}
            <div className="flex-1 flex flex-col min-w-0">
              <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white">{title}</h2>
                  <div className="text-xs text-slate-500 mt-1">Attribut #{attributeId}</div>
                </div>
                <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                  <span className="material-icons-outlined">close</span>
                </button>
              </div>

          <div className="p-6 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/30">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <DateTimePicker
                value={dateFrom}
                onChange={handleDateFromChange}
              />
              <DateTimePicker
                value={dateTo}
                onChange={handleDateToChange}
              />
              <div className="flex items-end gap-2 flex-wrap">
                <button
                  onClick={() => {
                    const now = new Date();
                    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
                    const fromStr = yesterday.toISOString().slice(0, 16);
                    const toStr = now.toISOString().slice(0, 16);
                    setDateFrom(fromStr);
                    setDateTo(toStr);
                    setDateError(null);
                  }}
                  className="px-4 py-2 bg-primary/20 border border-primary/30 text-primary rounded-lg text-sm font-semibold hover:bg-primary/30 transition-colors whitespace-nowrap"
                >
                  Dernières 24h
                </button>
                <button
                  onClick={() => refetch()}
                  disabled={!!dateError || isExporting}
                  className={`px-4 py-2 border rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 whitespace-nowrap ${
                    dateError || isExporting
                      ? 'bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-400 dark:text-slate-500 cursor-not-allowed'
                      : 'bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600'
                  }`}
                >
                  <span className="material-icons-outlined" style={{ fontSize: '18px' }}>refresh</span>
                  Actualiser
                </button>
                {!isExporting && (
                  <button
                    onClick={handleExport}
                    disabled={!!dateError || !fromISO || !toISO}
                    className={`px-4 py-2 border rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 whitespace-nowrap ${
                      dateError || !fromISO || !toISO
                        ? 'bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-400 dark:text-slate-500 cursor-not-allowed opacity-60'
                        : 'bg-emerald-600/20 border-emerald-500/30 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-600/30'
                    }`}
                  >
                    <span className="material-icons-outlined" style={{ fontSize: '18px' }}>download</span>
                    <span>Exporter</span>
                  </button>
                )}
              </div>
            </div>
            {isExporting && (
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-slate-500 dark:text-slate-400">Génération du fichier CSV...</span>
                  <span className="text-xs text-slate-500 dark:text-slate-400">{Math.round(exportProgress)}%</span>
                </div>
                <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-primary h-full transition-all duration-300 ease-out rounded-full"
                    style={{ width: `${exportProgress}%` }}
                  />
                </div>
              </div>
            )}
            {dateError && (
              <div className="mt-3 p-3 bg-rose-500/10 border border-rose-500/20 rounded-lg">
                <div className="flex items-center gap-2 text-rose-400 text-sm">
                  <span className="material-icons-outlined" style={{ fontSize: '18px' }}>error</span>
                  <span>{dateError}</span>
                </div>
              </div>
            )}
          </div>

          <div className="flex-1 overflow-auto p-6">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <span className="material-icons-outlined animate-spin text-primary text-4xl">progress_activity</span>
              </div>
            ) : chartData.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500 dark:text-slate-400 gap-2">
                <span>Aucune donnée disponible</span>
                {(attribute?.currentValue != null && attribute.currentValue !== '') || attribute?.currentValueTs ? (
                  <div className="text-sm text-slate-500 dark:text-slate-400 text-center">
                    {attribute?.currentValue != null && attribute.currentValue !== '' && (
                      <div>Dernière valeur : <span className="font-semibold text-slate-700 dark:text-slate-300">{attribute.currentValue}{unit ? ` ${unit}` : ''}</span></div>
                    )}
                    {attribute?.currentValueTs && (
                      <div>Date : {formatParisDateTime(attribute.currentValueTs)}</div>
                    )}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                    <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Valeur actuelle</div>
                    <div className="text-2xl font-bold text-slate-900 dark:text-white">
                      {chartData[chartData.length - 1]?.value.toFixed(2)}{unit ? ` ${unit}` : ''}
                    </div>
                  </div>
                  <div className="bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                    <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Minimum</div>
                    <div className="text-2xl font-bold text-slate-900 dark:text-white">
                      {dataMin !== null ? `${dataMin.toFixed(2)}${unit ? ` ${unit}` : ''}` : '—'}
                    </div>
                  </div>
                  <div className="bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                    <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Maximum</div>
                    <div className="text-2xl font-bold text-slate-900 dark:text-white">
                      {dataMax !== null ? `${dataMax.toFixed(2)}${unit ? ` ${unit}` : ''}` : '—'}
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg p-6 overflow-x-auto">
                  <svg
                    viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                    className="w-full h-auto min-w-[800px] cursor-crosshair"
                    onMouseMove={handleChartMouseMove}
                    onMouseLeave={handleChartMouseLeave}
                  >
                    {/* Grid lines */}
                    {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
                      const y = padding + ratio * (chartHeight - 2 * padding);
                      const value = minValue + (1 - ratio) * range;
                      return (
                        <g key={ratio}>
                          <line x1={padding} y1={y} x2={chartWidth - padding} y2={y} stroke={isDark ? 'rgba(148, 163, 184, 0.1)' : 'rgba(100, 116, 139, 0.15)'} strokeWidth="1" />
                          <text x={padding - 10} y={y + 4} fill={isDark ? 'rgb(148, 163, 184)' : 'rgb(71, 85, 105)'} fontSize="12" textAnchor="end">
                            {value.toFixed(1)}{unit ? ` ${unit}` : ''}
                          </text>
                        </g>
                      );
                    })}

                    {/* X axis labels (dates) */}
                    {(() => {
                      if (chartData.length < 2) return null;
                      const indices = [
                        0,
                        Math.round((chartData.length - 1) * 0.25),
                        Math.round((chartData.length - 1) * 0.5),
                        Math.round((chartData.length - 1) * 0.75),
                        chartData.length - 1,
                      ];
                      const uniq = Array.from(new Set(indices)).sort((a, b) => a - b);
                      const y = chartHeight - padding + 22;
                      return (
                        <g>
                          <line x1={padding} y1={chartHeight - padding} x2={chartWidth - padding} y2={chartHeight - padding} stroke={isDark ? 'rgba(148, 163, 184, 0.2)' : 'rgba(100, 116, 139, 0.2)'} strokeWidth="1" />
                          {uniq.map((i) => {
                            const p = chartData[i];
                            const x = getX(i);
                            const textAnchor = i === 0 ? 'start' : i === chartData.length - 1 ? 'end' : 'middle';
                            const label = formatParisDateTime(p.timestamp, {
                              day: '2-digit',
                              month: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                            });
                            return (
                              <g key={i}>
                                <line x1={x} y1={chartHeight - padding} x2={x} y2={chartHeight - padding + 6} stroke={isDark ? 'rgba(148, 163, 184, 0.25)' : 'rgba(100, 116, 139, 0.25)'} strokeWidth="1" />
                                <text x={x} y={y} fill={isDark ? 'rgb(148, 163, 184)' : 'rgb(71, 85, 105)'} fontSize="12" textAnchor={textAnchor}>
                                  {label}
                                </text>
                              </g>
                            );
                          })}
                        </g>
                      );
                    })()}

                    {/* Area fill */}
                    {pathData && (
                      <path
                        d={`${pathData} L ${getX(chartData.length - 1)} ${chartHeight - padding} L ${padding} ${chartHeight - padding} Z`}
                        fill="url(#gradient)"
                        opacity="0.2"
                      />
                    )}

                    {/* Line */}
                    {pathData && (
                      <path
                        d={pathData}
                        fill="none"
                        stroke="rgb(34, 211, 238)"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    )}

                    {/* Hover marker + tooltip */}
                    {hoveredPoint && (
                      <g>
                        <line
                          x1={hoveredPoint.x}
                          y1={padding}
                          x2={hoveredPoint.x}
                          y2={chartHeight - padding}
                          stroke={isDark ? 'rgba(148, 163, 184, 0.35)' : 'rgba(100, 116, 139, 0.35)'}
                          strokeWidth="1"
                          strokeDasharray="4 4"
                        />
                        <circle cx={hoveredPoint.x} cy={hoveredPoint.y} r={5} fill="rgb(34, 211, 238)" stroke={isDark ? 'rgb(15, 23, 42)' : 'rgb(248, 250, 252)'} strokeWidth="2" />

                        {(() => {
                          const outOfRange = hoveredPoint.value < minValue || hoveredPoint.value > maxValue;
                          const tooltipText = `${hoveredPoint.value.toFixed(2)}${unit ? ` ${unit}` : ''}${outOfRange ? ' (hors plage)' : ''} • ${hoveredPoint.label}`;
                          const boxWidth = 240;
                          const boxHeight = 34;
                          const margin = 8;
                          const x = Math.min(chartWidth - padding - boxWidth, Math.max(padding, hoveredPoint.x - boxWidth / 2));
                          const y = Math.max(margin, hoveredPoint.y - boxHeight - 10);
                          return (
                            <g>
                              <rect x={x} y={y} width={boxWidth} height={boxHeight} rx={8} fill={isDark ? 'rgba(15, 23, 42, 0.92)' : 'rgba(248, 250, 252, 0.95)'} stroke={isDark ? 'rgba(148, 163, 184, 0.35)' : 'rgba(100, 116, 139, 0.35)'} />
                              <text x={x + 10} y={y + 22} fill={isDark ? 'rgb(226, 232, 240)' : 'rgb(51, 65, 85)'} fontSize="12">
                                {tooltipText}
                              </text>
                            </g>
                          );
                        })()}
                      </g>
                    )}

                    <defs>
                      <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="rgb(34, 211, 238)" stopOpacity="0.3" />
                        <stop offset="100%" stopColor="rgb(34, 211, 238)" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
              </div>
            )}
          </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 text-slate-900 dark:bg-slate-900 dark:text-slate-100 h-screen flex overflow-hidden font-display">
      {/* Sidebar */}
      <UserSidebar projectId={projectIdInt} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative bg-slate-50 dark:bg-slate-900">
        {/* Header */}
        <header className="h-16 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between px-6 sm:px-8 z-20 shadow-sm sticky top-0">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 text-slate-400 hover:text-slate-900 dark:hover:text-white rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              <span className="material-icons-outlined" style={{ fontSize: '20px' }}>menu</span>
            </button>
            <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100 tracking-tight flex items-center gap-2">
              Carte
            </h1>
            <div className="h-6 w-px bg-slate-200 dark:bg-slate-700 mx-2"></div>
            <div className="flex items-center text-sm text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800/50 px-3 py-1.5 rounded-md border border-slate-200 dark:border-slate-700/50">
              <span className="material-icons-outlined mr-2 text-slate-500 dark:text-slate-500" style={{ fontSize: '16px' }}>domain</span>
              {currentProject.title} • {currentProject.city || 'Paris'}
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="hidden md:flex items-center px-3 py-1 bg-slate-100 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-full text-xs font-medium text-slate-600 dark:text-slate-300">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse shadow-sm shadow-green-500/50"></span>
              Système en ligne
            </div>
            <div className="h-8 w-px bg-slate-200 dark:bg-slate-700 mx-1"></div>
            <ProjectLogo project={currentProject} size="md" className="rounded-lg" />
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-primary transition-colors border border-transparent hover:border-slate-200 dark:hover:border-slate-700"
              title={isDark ? 'Activer le mode clair' : 'Activer le mode sombre'}
            >
              {isDark ? <span className="material-icons-outlined">light_mode</span> : <span className="material-icons-outlined">dark_mode</span>}
            </button>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-hidden relative bg-slate-50 dark:bg-slate-900">
          <main className="h-full w-full relative">
            {/* Project Name Badge - Center Top */}
            {currentProject && (
              <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-30 bg-white/95 dark:bg-slate-800/90 backdrop-blur-sm border border-slate-200 dark:border-slate-700 rounded-lg px-5 py-3 shadow-lg">
                <div className="flex items-center gap-3">
                  <span className="material-icons-outlined text-primary" style={{ fontSize: '22px' }}>domain</span>
                  <span className="text-base font-semibold text-slate-900 dark:text-white">{currentProject.title}</span>
                  {currentProject.city && (
                    <>
                      <span className="text-slate-500">•</span>
                      <span className="text-sm text-slate-500 dark:text-slate-400">{currentProject.city}</span>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Weather Card - Top Right */}
            {currentProject?.city && (
              <div className="absolute top-4 right-4 z-30 bg-gradient-to-br from-white/95 via-slate-50/95 to-slate-100/95 dark:from-slate-800/95 dark:via-slate-800/90 dark:to-slate-900/95 backdrop-blur-md border border-slate-200/80 dark:border-slate-700/80 rounded-xl px-5 py-4 shadow-2xl min-w-[220px]">
                {isLoadingWeather ? (
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <span className="material-icons-outlined animate-spin text-primary" style={{ fontSize: '20px' }}>progress_activity</span>
                      <div className="absolute inset-0 bg-primary/20 rounded-full blur-sm"></div>
                    </div>
                    <span className="text-sm text-slate-600 dark:text-slate-300 font-medium">Chargement météo...</span>
                  </div>
                ) : weatherError ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-red-500/10 rounded-lg border border-red-500/20">
                        <span className="material-icons-outlined text-red-400" style={{ fontSize: '20px' }}>cloud_off</span>
                      </div>
                      <span className="text-sm text-slate-600 dark:text-slate-300 font-medium">Météo indisponible</span>
                    </div>
                    {weatherError instanceof Error && (
                      <div className="text-xs text-slate-500 mt-2 pl-11">
                        {weatherError.message.includes('non trouvée') ? (
                          <span>Ville "{currentProject.city}" introuvable</span>
                        ) : (
                          <span className="text-slate-600 dark:text-slate-400">{weatherError.message}</span>
                        )}
                      </div>
                    )}
                  </div>
                ) : weatherData ? (
                  <div className="space-y-3">
                    {weatherData.current_condition && weatherData.current_condition[0] && (() => {
                      const current = weatherData.current_condition[0];
                      const cityName = weatherData.nearest_area?.[0]?.areaName?.[0]?.value || currentProject.city;
                      const iconUrl = current.weatherIconUrl?.[0]?.value;
                      const description = current.weatherDesc?.[0]?.value || '';
                      const temp = parseInt(current.temp_C || '0', 10);
                      const feelsLike = parseInt(current.FeelsLikeC || '0', 10);
                      const humidity = current.humidity || '0';
                      const windSpeed = current.windspeedKmph || '0';

                      // Determine color based on temperature
                      const getTempColor = (t: number) => {
                        if (t >= 30) return 'from-orange-500/20 to-red-500/20 text-orange-300';
                        if (t >= 25) return 'from-amber-500/20 to-orange-500/20 text-amber-300';
                        if (t >= 20) return 'from-yellow-500/20 to-amber-500/20 text-yellow-300';
                        if (t >= 15) return 'from-green-500/20 to-yellow-500/20 text-green-300';
                        if (t >= 10) return 'from-cyan-500/20 to-green-500/20 text-cyan-300';
                        if (t >= 5) return 'from-blue-500/20 to-cyan-500/20 text-blue-300';
                        return 'from-indigo-500/20 to-blue-500/20 text-indigo-300';
                      };

                      const tempColorClass = getTempColor(temp);

                      return (
                        <>
                          {/* Header with city and icon */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="p-1.5 bg-primary/10 rounded-lg border border-primary/20">
                                <span className="material-icons-outlined text-primary" style={{ fontSize: '16px' }}>location_on</span>
                              </div>
                              <span className="text-sm text-slate-700 dark:text-slate-300 font-semibold">{cityName}</span>
                            </div>
                            {iconUrl && (
                              <div className="relative">
                                <img
                                  src={iconUrl}
                                  alt={description}
                                  className="w-12 h-12 drop-shadow-lg"
                                />
                                <div className="absolute inset-0 bg-white/10 rounded-full blur-sm"></div>
                              </div>
                            )}
                          </div>

                          {/* Temperature display */}
                          <div className={`relative bg-gradient-to-br ${tempColorClass} rounded-xl p-4 border border-white/10`}>
                            <div className="flex items-baseline gap-2">
                              <span className="text-4xl font-bold text-white drop-shadow-lg">{temp}°</span>
                              <span className="text-lg text-white/80 font-medium">C</span>
                            </div>
                            <div className="absolute top-2 right-2 opacity-20">
                              <span className="material-icons-outlined text-white" style={{ fontSize: '32px' }}>wb_sunny</span>
                            </div>
                          </div>

                          {/* Details grid */}
                          <div className="grid grid-cols-2 gap-2">
                            <div className="bg-slate-100/80 dark:bg-slate-900/40 rounded-lg p-2 border border-slate-200/50 dark:border-slate-700/50">
                              <div className="flex items-center gap-1.5 mb-1">
                                <span className="material-icons-outlined text-amber-400" style={{ fontSize: '16px' }}>thermostat</span>
                                <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">Ressenti</span>
                              </div>
                              <span className="text-sm font-semibold text-slate-900 dark:text-white">{feelsLike}°C</span>
                            </div>
                            <div className="bg-slate-100/80 dark:bg-slate-900/40 rounded-lg p-2 border border-slate-200/50 dark:border-slate-700/50">
                              <div className="flex items-center gap-1.5 mb-1">
                                <span className="material-icons-outlined text-blue-400" style={{ fontSize: '16px' }}>water_drop</span>
                                <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">Humidité</span>
                              </div>
                              <span className="text-sm font-semibold text-slate-900 dark:text-white">{humidity}%</span>
                            </div>
                            <div className="bg-slate-100/80 dark:bg-slate-900/40 rounded-lg p-2 border border-slate-200/50 dark:border-slate-700/50 col-span-2">
                              <div className="flex items-center gap-1.5 mb-1">
                                <span className="material-icons-outlined text-cyan-400" style={{ fontSize: '16px' }}>air</span>
                                <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">Vent</span>
                              </div>
                              <span className="text-sm font-semibold text-slate-900 dark:text-white">{windSpeed} km/h</span>
                            </div>
                          </div>

                          {/* Description */}
                          {description && (
                            <div className="pt-2 border-t border-slate-200/50 dark:border-slate-700/50">
                              <div className="flex items-center gap-2">
                                <span className="material-icons-outlined text-slate-500 dark:text-slate-400" style={{ fontSize: '14px' }}>info</span>
                                <span className="text-xs text-slate-600 dark:text-slate-300 capitalize font-medium">{description}</span>
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                ) : null}
              </div>
            )}

            {/* Zoom Controls */}
            <div className="absolute top-4 left-4 z-30 flex flex-col gap-2 bg-white/95 dark:bg-slate-800/90 border border-slate-200 dark:border-slate-700 rounded-lg p-2 shadow-lg">
              <button
                onClick={handleZoomIn}
                className="p-2 rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 dark:hover:text-white transition-colors"
                title="Zoom avant"
              >
                <span className="material-icons-outlined" style={{ fontSize: '20px' }}>zoom_in</span>
              </button>
              <button
                onClick={handleZoomOut}
                className="p-2 rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 dark:hover:text-white transition-colors"
                title="Zoom arrière"
              >
                <span className="material-icons-outlined" style={{ fontSize: '20px' }}>zoom_out</span>
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleResetZoom();
                }}
                className="p-2 rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 dark:hover:text-white transition-colors"
                title="Réinitialiser"
              >
                <span className="material-icons-outlined" style={{ fontSize: '20px' }}>fit_screen</span>
              </button>
              <div className="h-px bg-slate-200 dark:bg-slate-700 my-1"></div>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleMarkerMovement();
                }}
                className={`p-2 rounded-lg transition-colors ${
                  isMovingMarker
                    ? 'bg-primary hover:bg-primary/80 text-white'
                    : 'bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 dark:hover:text-white'
                }`}
                title={isMovingMarker ? 'Cliquez sur un bâtiment pour le déplacer' : 'Déplacer les marqueurs'}
              >
                <span className="material-icons-outlined" style={{ fontSize: '20px' }}>
                  {isMovingMarker ? 'check' : 'edit_location'}
                </span>
              </button>
              <div className="px-2 py-1 text-xs text-slate-500 dark:text-slate-400 text-center border-t border-slate-200 dark:border-slate-700 mt-1">
                {Math.round(zoomLevel * 100)}%
              </div>
            </div>

            {/* Project Logo - Bottom Left */}
            {currentProject && (
              <div className="absolute bottom-4 left-4 z-20 bg-white/95 backdrop-blur-sm border border-slate-300 rounded-lg p-3 shadow-lg">
                <ProjectLogo project={currentProject} size="lg" className="rounded-lg" />
              </div>
            )}

            {/* Image Container */}
            <div
              ref={imageContainerRef}
              className="h-full w-full overflow-hidden relative bg-slate-200 dark:bg-slate-900"
              style={{
                cursor: isMovingMarker ? 'crosshair' : (isDragging ? 'grabbing' : 'grab'),
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none',
                touchAction: 'none', // Prevent touch scrolling
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              {!mapImageUrl ? (
                // No image configured for this project
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center max-w-md px-6">
                    <span className="material-icons-outlined text-slate-500 text-6xl mb-4">map</span>
                    <h3 className="text-xl font-semibold text-slate-700 dark:text-slate-300 mb-2">Aucune image de carte</h3>
                    <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">
                      Ce projet n'a pas d'image de carte configurée. Contactez un administrateur pour en ajouter une.
                    </p>
                  </div>
                </div>
              ) : imageError ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <span className="material-icons-outlined text-red-400 text-6xl mb-4">error</span>
                    <p className="text-red-400">Erreur de chargement de l'image de carte</p>
                    <p className="text-slate-500 dark:text-slate-400 text-sm mt-2">Vérifiez l’image configurée pour ce projet (admin) ou le fallback local.</p>
                  </div>
                </div>
              ) : (
                <>
                  {!imageLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center z-50 bg-slate-200 dark:bg-slate-900">
                      <div className="text-center">
                        <span className="material-icons-outlined animate-spin text-primary text-6xl mb-4">progress_activity</span>
                        <p className="text-slate-600 dark:text-slate-400">Chargement de l'image...</p>
                      </div>
                    </div>
                  )}
                  <div
                    className="absolute inset-0 flex items-center justify-center"
                    style={{
                      transform: `translate(${panX}px, ${panY}px) scale(${zoomLevel})`,
                      transformOrigin: 'center center',
                      width: '100%',
                      height: '100%',
                      transition: isDragging ? 'none' : 'transform 0.2s ease-out',
                    }}
                  >
                    <div className="relative">
                      {/* Only render image if mapImageUrl exists */}
                      {mapImageUrl && currentProject && (
                        <img
                          key={`img-${currentProject.id}-${currentProject.mapImage}-${projectChangeTimestamp}`}
                          src={mapImageUrl}
                          alt="Carte de la zone"
                          className="select-none"
                          draggable={false}
                          style={{
                            maxHeight: '100vh',
                            maxWidth: '100vw',
                            height: 'auto',
                            width: 'auto',
                            display: imageLoaded ? 'block' : 'none',
                            objectFit: 'contain',
                          }}
                          onError={(e) => {
                            console.error('Erreur de chargement de l\'image:', e);
                            console.error('URL de l\'image:', e.currentTarget.src);
                            setImageError(true);
                            setImageLoaded(false);
                          }}
                          onLoad={(e) => {
                            console.log('[CartePage] Image loaded:', e.currentTarget.src);
                            setImageLoaded(true);
                            setImageError(false);
                          }}
                        />
                      )}
                      {imageLoaded && (
                        <>
                          {/* Building markers */}
                          {buildings.map((building) => {
                            const position = buildingPositions.get(building.id);
                            if (!position) return null;
                            
                            const isMovingThis = isMovingMarker && movingBuildingId === building.id;
                            
                            return (
                              <div
                                key={building.id}
                                className="absolute z-10"
                                style={{
                                  left: `${position.x * 100}%`,
                                  top: `${position.y * 100}%`,
                                  transform: 'translate(-50%, -100%)',
                                  pointerEvents: 'auto',
                                }}
                                onClick={(e) => {
                                  handleBuildingMarkerClick(building, e);
                                }}
                              >
                                <div className="flex flex-col items-center group">
                                  {/* Building card/marker */}
                                  <div className={`bg-gradient-to-br rounded-lg shadow-xl ring-4 cursor-pointer transition-all transform hover:scale-105 px-4 py-2 min-w-[120px] ${
                                    isMovingThis 
                                      ? 'from-yellow-500 to-yellow-600 ring-yellow-500/50 animate-pulse' 
                                      : isMovingMarker
                                        ? 'from-blue-400 to-blue-500 ring-blue-400/30 opacity-75'
                                        : 'from-blue-500 to-blue-600 ring-blue-500/30 hover:from-blue-600 hover:to-blue-700'
                                  }`}>
                                    <div className="flex items-center gap-2">
                                      <span className="material-icons-outlined text-white" style={{ fontSize: '20px' }}>
                                        domain
                                      </span>
                                      <span className="text-white font-semibold text-sm whitespace-nowrap">
                                        {building.name}
                                      </span>
                                    </div>
                                    <div className="mt-1 flex items-center gap-1">
                                      {isMovingMarker ? (
                                        <>
                                          <span className="material-icons-outlined text-white/80" style={{ fontSize: '14px' }}>
                                            {isMovingThis ? 'touch_app' : 'edit_location'}
                                          </span>
                                          <span className="text-white/80 text-xs">
                                            {isMovingThis ? 'Cliquez pour placer' : 'Sélectionner'}
                                          </span>
                                        </>
                                      ) : (
                                        <>
                                          <span className="material-icons-outlined text-white/80" style={{ fontSize: '14px' }}>
                                            info
                                          </span>
                                          <span className="text-white/80 text-xs">Cliquez pour voir</span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  {/* Arrow pointer */}
                                  <div className={`w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent -mt-1 ${
                                    isMovingThis 
                                      ? 'border-t-yellow-500' 
                                      : isMovingMarker
                                        ? 'border-t-blue-400'
                                        : 'border-t-blue-500'
                                  }`}></div>
                                </div>
                              </div>
                            );
                          })}
                        </>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </main>
        </div>

        {/* Marker Sidebar */}
        {markerSidebarOpen && (
          <div className="fixed inset-0 z-50 flex">
            {/* Overlay */}
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => {
                setMarkerSidebarOpen(false);
                setSelectedBuilding(null);
                setSelectedFloorId(null);
              }}
            />
            
            {/* Sidebar */}
            <div className="absolute right-6 top-6 bottom-6 w-[480px] bg-white/95 dark:bg-slate-900/85 backdrop-blur-md border border-slate-200 dark:border-slate-700/50 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
              {/* Header */}
              <div className="p-6 border-b border-slate-200 dark:border-slate-700/50">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                      {selectedBuilding ? selectedBuilding.name : 'Sélectionner un bâtiment'}
                    </h2>
                    {selectedBuilding && (
                      <p className="text-sm text-slate-600 dark:text-slate-300">Liste des appartements</p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setMarkerSidebarOpen(false);
                      setSelectedBuilding(null);
                      setSelectedFloorId(null);
                      setApartmentSearch('');
                    }}
                    className="p-2 rounded-lg text-slate-500 dark:text-white hover:bg-slate-100 dark:hover:bg-white/10 transition-colors"
                  >
                    <span className="material-icons-outlined" style={{ fontSize: '24px' }}>close</span>
                  </button>
                </div>
                
                {/* Building KPIs */}
                {selectedBuilding && (
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-slate-100 dark:bg-slate-800/60 rounded-xl p-4 border border-slate-200 dark:border-slate-700/50">
                      <div className="text-3xl font-bold text-slate-900 dark:text-white">
                        {buildingStats?.totalItems ?? 0}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-300 mt-1 uppercase tracking-wider">LOGEMENTS</div>
                    </div>
                    <div className="bg-slate-100 dark:bg-slate-800/60 rounded-xl p-4 border border-slate-200 dark:border-slate-700/50">
                      <div className={`text-3xl font-bold ${offlineBoxesCount > 0 ? 'text-red-500 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                        {offlineBoxesCount}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-300 mt-1 uppercase tracking-wider">BOX OFFLINE</div>
                    </div>
                  </div>
                )}

                {/* Search bar */}
                {selectedBuilding && (
                  <div className="relative">
                    <span className="material-icons-outlined absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" style={{ fontSize: '20px' }}>
                      search
                    </span>
                    <input
                      type="text"
                      placeholder="Rechercher par nom ou numéro d'appartement..."
                      value={apartmentSearch}
                      onChange={(e) => setApartmentSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-slate-100 dark:bg-slate-800/60 border border-slate-300 dark:border-slate-700/50 rounded-lg text-slate-800 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-transparent"
                    />
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto px-6 py-4">
                {isLoadingBuildings ? (
                  <div className="flex items-center justify-center py-8">
                    <span className="material-icons-outlined animate-spin text-primary text-2xl">progress_activity</span>
                  </div>
                ) : !selectedBuilding ? (
                  <div className="space-y-2">
                    {buildings.length === 0 ? (
                      <p className="text-center text-slate-500 dark:text-slate-400 py-8">Aucun bâtiment disponible</p>
                    ) : (
                      buildings.map((building) => (
                        <button
                          key={building.id}
                          onClick={() => setSelectedBuilding(building)}
                          className="w-full p-3 bg-slate-100 dark:bg-slate-700/50 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-700 text-left transition-colors"
                        >
                          <span className="font-medium text-slate-800 dark:text-slate-200">{building.name}</span>
                        </button>
                      ))
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {apartmentSearch.trim().length > 0 && apartmentSearch.trim().length < 3 && (
                      <div className="text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 rounded-lg p-3">
                        Tapez au moins 3 caractères pour lancer la recherche.
                      </div>
                    )}

                    {isSearchMode ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-slate-800 dark:text-slate-200">Résultats</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            {debouncedApartmentSearch.trim().length >= 3 ? `${searchedApartments.length} appartement(s)` : ''}
                          </p>
                        </div>

                        {isLoadingSearchApartments ? (
                          <div className="flex items-center justify-center py-6">
                            <span className="material-icons-outlined animate-spin text-primary">progress_activity</span>
                          </div>
                        ) : searchedApartments.length === 0 ? (
                          <p className="text-sm text-slate-500 dark:text-slate-400 italic">Aucun appartement trouvé</p>
                        ) : (
                          <div className="space-y-2">
                            {searchedApartments.map((apartment) => {
                              const tempInterieur = apartmentTemperatures.get(apartment.id);
                              let dotColorClass = '';
                              if (tempInterieur !== null && tempInterieur !== undefined) {
                                if (tempInterieur < 20) {
                                  dotColorClass = 'bg-blue-500';
                                } else if (tempInterieur <= 22) {
                                  dotColorClass = 'bg-orange-500';
                                } else {
                                  dotColorClass = 'bg-red-500';
                                }
                              }
                              return (
                                <div
                                  key={apartment.id}
                                  onClick={() => {
                                    setSelectedApartment(apartment);
                                    setIsApartmentModalOpen(true);
                                  }}
                                  className="p-2 bg-slate-100 dark:bg-slate-800/50 rounded border border-slate-200 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="text-slate-800 dark:text-slate-200 font-medium">{apartment.name}</span>
                                    <div className="flex items-center gap-2">
                                      {tempInterieur !== null && tempInterieur !== undefined ? (
                                        <div className="flex items-center gap-1.5">
                                          <span className="text-xs text-slate-600 dark:text-slate-300 font-medium">
                                            {tempInterieur.toFixed(1)}°C
                                          </span>
                                          <div className={`w-2 h-2 rounded-full ${dotColorClass}`}></div>
                                        </div>
                                      ) : (
                                        <span className="text-xs text-slate-500 dark:text-slate-400">
                                          N/A
                                        </span>
                                      )}
                                      {apartment.typologie && (
                                        <span className="text-xs text-slate-600 dark:text-slate-300 bg-slate-200 dark:bg-slate-600 px-2 py-1 rounded">
                                          {apartment.typologie}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  {apartment.surface && (
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{apartment.surface} m²</p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        {/* Floors */}
                        {isLoadingFloors ? (
                          <div className="flex items-center justify-center py-4">
                            <span className="material-icons-outlined animate-spin text-primary">progress_activity</span>
                          </div>
                        ) : floors.length === 0 ? (
                          <p className="text-center text-slate-500 dark:text-slate-400 py-8">Aucun étage disponible</p>
                        ) : (
                          floors.map((floor) => (
                            <div key={floor.id} className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                              <button
                                onClick={() => setSelectedFloorId(selectedFloorId === floor.id ? null : floor.id)}
                                className="w-full flex items-center justify-between p-3 bg-slate-100 dark:bg-slate-700/50 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors text-left"
                              >
                                <span className="font-medium text-slate-800 dark:text-slate-200">{floor.name}</span>
                                <span className="material-icons-outlined text-slate-500 dark:text-slate-400" style={{ fontSize: '20px' }}>
                                  {selectedFloorId === floor.id ? 'expand_less' : 'expand_more'}
                                </span>
                              </button>

                              {selectedFloorId === floor.id && (
                                <div className="p-3 bg-slate-50 dark:bg-slate-900/50 space-y-2">
                                  {isLoadingFloorApartments ? (
                                    <div className="flex items-center justify-center py-4">
                                      <span className="material-icons-outlined animate-spin text-primary">progress_activity</span>
                                    </div>
                                  ) : floorApartments.length === 0 ? (
                                    <p className="text-sm text-slate-500 dark:text-slate-400 italic">Aucun appartement</p>
                                  ) : (
                                    floorApartments.map((apartment) => {
                                      const tempInterieur = apartmentTemperatures.get(apartment.id);
                                      let dotColorClass = '';
                                      if (tempInterieur !== null && tempInterieur !== undefined) {
                                        if (tempInterieur < 20) {
                                          dotColorClass = 'bg-blue-500';
                                        } else if (tempInterieur <= 22) {
                                          dotColorClass = 'bg-orange-500';
                                        } else {
                                          dotColorClass = 'bg-red-500';
                                        }
                                      }
                                      return (
                                        <div
                                          key={apartment.id}
                                          onClick={() => {
                                            setSelectedApartment(apartment);
                                            setIsApartmentModalOpen(true);
                                          }}
                                          className="p-2 bg-slate-100 dark:bg-slate-800/50 rounded border border-slate-200 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                                        >
                                          <div className="flex items-center justify-between">
                                            <span className="text-slate-800 dark:text-slate-200 font-medium">{apartment.name}</span>
                                            <div className="flex items-center gap-2">
                                              {tempInterieur !== null && tempInterieur !== undefined ? (
                                                <div className="flex items-center gap-1.5">
                                                  <span className="text-xs text-slate-600 dark:text-slate-300 font-medium">
                                                    {tempInterieur.toFixed(1)}°C
                                                  </span>
                                                  <div className={`w-2 h-2 rounded-full ${dotColorClass}`}></div>
                                                </div>
                                              ) : (
                                                <span className="text-xs text-slate-500 dark:text-slate-400">
                                                  N/A
                                                </span>
                                              )}
                                              {apartment.typologie && (
                                                <span className="text-xs text-slate-600 dark:text-slate-300 bg-slate-200 dark:bg-slate-600 px-2 py-1 rounded">
                                                  {apartment.typologie}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                          {apartment.surface && (
                                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{apartment.surface} m²</p>
                                          )}
                                        </div>
                                      );
                                    })
                                  )}
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Stream Modal - Opens directly when apartment is selected */}
        {isApartmentModalOpen && isLoadingSupervision && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="flex items-center justify-center">
              <span className="material-icons-outlined animate-spin text-primary text-4xl">progress_activity</span>
            </div>
          </div>
        )}

        {/* Stream Modal */}
        {modalOpen && modalAttributeId && modalApartment && currentProject && (
          <StreamModal
            key={`${modalInstance}-${modalAttributeId}`}
            attributeId={modalAttributeId}
            title={modalTitle}
            apartment={modalApartment}
            projectId={currentProject.id}
            onClose={() => {
              setModalOpen(false);
              setModalApartment(null);
              setIsApartmentModalOpen(false);
              setSelectedApartment(null);
            }}
            onAttributeChange={(attrId, attrTitle) => {
              setModalAttributeId(attrId);
              setModalTitle(attrTitle);
              setModalInstance((v) => v + 1);
            }}
          />
        )}
      </div>
    </div>
  );
}
